import Helper from 'library/Helper';
import moment from 'moment';
import { dockMappingToApiArray, dockMappingFromApiArray } from 'components/address/DockVehicleMapping';

const safeParseInt = (val: any): number | null => {
    if (val == null) return null;
    const parsed = parseInt(val, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

export type ExternalAddress = {
    name: string,
    phone: string | null | undefined,
    alternate_phone: string | null | undefined,
    alternate_phone_2: string | null | undefined,
    address_line_1: string,
    address_line_2: string,
    address_line_3: string | null | undefined,
    address_line_4: string | null | undefined,
    address_line_5: string | null | undefined,
    state: string,
    city: string,
    pincode: string,
    country: string | null | undefined,
    latitude: string | null | undefined,
    longitude: string | null | undefined,
    email?: string | null | undefined,
    district?: string | null | undefined,
    zone?: string | null | undefined,
    area?: string | null | undefined,
    street_number?: string | null | undefined,
    company_name?: string | null | undefined,
    location_id?: string | null | undefined,
    location_code?: string | null | undefined,
    address_type?: string | null | undefined,
    national_destination_code?: string | null | undefined,
    account_id?: string | null | undefined,
    w3w_code?: string | null | undefined,
    address_hub_code?: string | null | undefined,
    service_time?: number | null | undefined,
    time_slot_list?: any[] | null | undefined,
    address_code?: string | null | undefined,
    notes?: string | null | undefined,
    grace_time?: number | null | undefined,
    dock_capacity?: number | null | undefined,
    dock_vehicle_mapping?: any | null | undefined,
};

export type PieceItemDetailInput = {
    pieceUniqueId?: string;
    hsCode?: string;
    itemNumber?: string;
    manufacturingCountry?: string;
    costPerPiece?: number;
    itemDescription?: string;
    quantity?: number;
    taxPercentage?: number;
    totalTax?: number;
};

export type PieceItemDetailOutput = {
    piece_unique_id?: string;
    hs_code?: string;
    item_number?: string;
    manufacturing_country?: string;
    cost_per_piece?: number;
    item_description?: string;
    quantity?: number;
    tax_percentage?: number;
    total_tax?: number;
};

export const transformAddressNew = (addressObj: any, options: any = {}): ExternalAddress => {
    return {
        name: addressObj.name,
        phone: addressObj.phone,
        alternate_phone: addressObj.alternatePhone,
        alternate_phone_2: addressObj.alternatePhone2,
        address_line_1: addressObj.addressLine1,
        address_line_2: addressObj.addressLine2,
        address_line_3: addressObj.addressLine3,
        address_line_4: addressObj.addressLine4,
        address_line_5: addressObj.addressLine5,
        state: addressObj.stateName,
        city: addressObj.cityName,
        pincode: addressObj.pincode,
        country: addressObj.countryName,
        latitude: addressObj.latitude != null ? String(addressObj.latitude) : '',
        longitude: addressObj.longitude != null ? String(addressObj.longitude) : '',
        email: addressObj.email,
        zone: addressObj.zone,
        district: addressObj.district,
        location_id: addressObj.locationId,
        location_code: addressObj.locationCode,
        address_type: addressObj.addressType,
        national_destination_code: addressObj.nationalDestinationCode,
        w3w_code: addressObj.w3wCode,
        address_hub_code: addressObj.addressHubCode,
        company_name: addressObj.companyName || options.companyName,
        service_time: addressObj.serviceTime,
        time_slot_list: addressObj.timeSlotList,
        address_code: addressObj.addressCode,
        notes: addressObj.notes,
        grace_time: safeParseInt(addressObj.graceTime),
        dock_capacity: safeParseInt(addressObj.dockCapacity),
        dock_vehicle_mapping: addressObj.dockVehicleMapping
            ? dockMappingToApiArray(addressObj.dockVehicleMapping as any)
            : addressObj.dockVehicleMapping,
    };
};

export const transformAddressFieldsForForm = (data: any, prefix: string) => ({
    name: data?.[`${prefix}_name`],
    phone: data?.[`${prefix}_phone`],
    alternatePhone: data?.[`${prefix}_alternate_phone`],
    alternatePhone2: data?.[`${prefix}_alternate_phone_2`],
    email: data?.[`${prefix}_email`],
    companyName: data?.[`${prefix}_company_name`],
    addressLine1: data?.[`${prefix}_address_line_1`],
    addressLine2: data?.[`${prefix}_address_line_2`],
    addressLine3: data?.[`${prefix}_address_line_3`],
    addressLine4: data?.[`${prefix}_address_line_4`],
    addressLine5: data?.[`${prefix}_address_line_5`],
    cityName: data?.[`${prefix}_city`],
    stateName: data?.[`${prefix}_state`],
    pincode: data?.[`${prefix}_pincode`],
    countryName: data?.[`${prefix}_country`],
    district: data?.[`${prefix}_district`],
    latitude: data?.[`${prefix}_geolocation`]?.length ? data[`${prefix}_geolocation`][0] : null,
    longitude: data?.[`${prefix}_geolocation`]?.length ? data[`${prefix}_geolocation`][1] : null,
    nationalDestinationCode: data?.[`${prefix}_national_destination_code`],
    addressType: data?.[`${prefix}_address_type`],
    serviceTime: data?.[`${prefix}_service_time`],
    timeSlotList: data?.[`${prefix}_time_slot_list`],
    graceTime: safeParseInt(data?.[`${prefix}_grace_time`]),
    dockCapacity: safeParseInt(data?.[`${prefix}_dock_capacity`]),
    dockVehicleMapping: Array.isArray(data?.[`${prefix}_dock_vehicle_mapping`])
        ? dockMappingFromApiArray(data[`${prefix}_dock_vehicle_mapping`])
        : data?.[`${prefix}_dock_vehicle_mapping`],
});

export const transformHighValue = (highValueDto: any) => {
    const response = {
        hv_id_num: highValueDto.idNum,
        hv_name: highValueDto.name,
        hv_id_expiry: highValueDto.idExpiry,
        hv_issue_place: highValueDto.issuePlace,
        hv_nationality: highValueDto.nationality,
        hv_phone: highValueDto.phone,
        hv_amount: highValueDto.amount,
        hv_billed_to_merchant: highValueDto.billedToMerchant,
        hv_hs_code: highValueDto.hsCode,
    };
    return response;
};

export const transformPieceItemDetailsNew = (pieceItemDetailsArr: PieceItemDetailInput[]): PieceItemDetailOutput[] => {
    return pieceItemDetailsArr.map((pieceItemDetailObj: PieceItemDetailInput) => {
        return {
            piece_unique_id: pieceItemDetailObj.pieceUniqueId,
            hs_code: pieceItemDetailObj.hsCode,
            item_number: pieceItemDetailObj.itemNumber,
            manufacturing_country: pieceItemDetailObj.manufacturingCountry,
            cost_per_piece: pieceItemDetailObj.costPerPiece,
            item_description: pieceItemDetailObj.itemDescription,
            quantity: pieceItemDetailObj.quantity,
            tax_percentage: pieceItemDetailObj.taxPercentage,
            total_tax: pieceItemDetailObj.totalTax,
        };
    });
};

const validatedCodAmount = (value: any) => {
    if (value === '' || value === null) {
        return null;
    }
    const result = Number(value);
    if (Number.isNaN(result)) {
        return value;
    }
    return result;
};

const computeVolumeInCcm = (l: any, w: any, h: any): number | undefined => {
    if (l === null || l === undefined || l === ''
        || w === null || w === undefined || w === ''
        || h === null || h === undefined || h === '') {
        return undefined;
    }
    const lNum = Number(l);
    const wNum = Number(w);
    const hNum = Number(h);
    if (!Number.isFinite(lNum) || !Number.isFinite(wNum) || !Number.isFinite(hNum)) {
        return undefined;
    }
    if (lNum <= 0 || wNum <= 0 || hNum <= 0) {
        return undefined;
    }
    return lNum * wNum * hNum;
};

export const transformPiecesDetailNew = (piecesDetailFromUpdatedData: any, options: any) => {
    const {
        courierType,
        isInternational,
        allowQuantityAsSinglePiece = false,
        allowMultiplePieceDimensions = false,
        isCustomForm = false,
        isServiceableForm = false,
        isStaticFormForPiecesDetail = false,
    } = options;

    const isDoc = (courierType || '').toLowerCase() === 'document';

    if (!isCustomForm && isInternational && isDoc) {
        return undefined;
    }

    // Conditional logic to handle static form too.
    if (!isCustomForm && !isServiceableForm && allowMultiplePieceDimensions && isStaticFormForPiecesDetail) {
        const transformedPieces: any = [];
        Object.keys(piecesDetailFromUpdatedData).forEach((key: any, index: number) => {
            const piece: any = piecesDetailFromUpdatedData[key];
            const lengthCm = Helper.converttoCentimeter(piece.length, piece.dimensionsUnit);
            const widthCm = Helper.converttoCentimeter(piece.width, piece.dimensionsUnit);
            const heightCm = Helper.converttoCentimeter(piece.height, piece.dimensionsUnit);
            const vol = computeVolumeInCcm(lengthCm, widthCm, heightCm);
            if (isInternational) {
                transformedPieces.push({
                    description: piece.description,
                    declaredValue: piece.declaredValue,
                    declared_value: piece.declaredValue,
                    reference_number: piece.reference_number,
                    item_details: Object.keys(piece.items || {}).map((itemKey) => {
                        return {
                            ...piece.items[itemKey],
                            pieceNumber: index,
                        };
                    }),
                    length: lengthCm,
                    width: widthCm,
                    height: heightCm,
                    pieceNumber: index,
                    piece_number: index,
                    weight: Helper.converttoKilogram(Number(piece.weight), piece.weightUnit),
                    volumetricWeight: piece.volumetricWeight,
                    volumetric_weight: piece.volumetricWeight,
                    dimension_unit: 'cm',
                    weight_unit: 'kg',
                    numberOfItems: Number(piece.numberOfPieces),
                    number_of_items: Number(piece.numberOfPieces),
                    volume: vol,
                    volume_unit: 'ccm',
                });
            } else if (allowQuantityAsSinglePiece) {
                for (let i = 0; i < Number(piece.numberOfPieces); i += 1) {
                    transformedPieces.push({
                        length: lengthCm,
                        width: widthCm,
                        height: heightCm,
                        item_quantity: 1,
                        quantity: 1,
                        reference_number: piece.reference_number,
                        dimension_unit: 'cm',
                        weight_unit: 'kg',
                        item_unit: piece.unit,
                        weight: Helper.converttoKilogram(piece.weight, piece.weightUnit),
                        weightUnit: 'kg',
                        volume: vol,
                        volume_unit: 'ccm',
                    });
                }
            } else {
                transformedPieces.push({
                    length: lengthCm,
                    width: widthCm,
                    height: heightCm,
                    item_quantity: Number(piece.numberOfPieces) || 1,
                    quantity: Number(piece.numberOfPieces) || 1,
                    item_unit: piece.unit,
                    weight: Helper.converttoKilogram(piece.weight, piece.weightUnit),
                    weightUnit: 'kg',
                    dimension_unit: 'cm',
                    reference_number: piece.reference_number,
                    weight_unit: 'kg',
                    cod_amount: validatedCodAmount(piece.codAmount),
                    volume: vol,
                    volume_unit: 'ccm',
                });
            }
        });
        return transformedPieces;
    }
    if (!isCustomForm && !Array.isArray(piecesDetailFromUpdatedData)) {
        return [];
    }


    return piecesDetailFromUpdatedData.map((piece: any) => {
        const dimUnit = piece.unit || piece.dimensionUnit;
        let lengthCm = piece.length;
        let widthCm = piece.width;
        let heightCm = piece.height;
        if (dimUnit) {
            if (piece.length !== null && piece.length !== undefined && piece.length !== '') {
                lengthCm = Helper.converttoCentimeter(piece.length, dimUnit);
            }
            if (piece.width !== null && piece.width !== undefined && piece.width !== '') {
                widthCm = Helper.converttoCentimeter(piece.width, dimUnit);
            }
            if (piece.height !== null && piece.height !== undefined && piece.height !== '') {
                heightCm = Helper.converttoCentimeter(piece.height, dimUnit);
            }
        }
        const vol = dimUnit ? computeVolumeInCcm(lengthCm, widthCm, heightCm) : undefined;
        return {
            reference_number: piece.reference_number || piece.pieceReferenceNumber,
            description: piece.description,
            piece_number: piece.pieceNumber,
            quantity: Number(piece.quantity),
            length: lengthCm,
            width: widthCm,
            height: heightCm,
            dimension_unit: dimUnit ? 'cm' : piece.dimensionUnit,
            weight: piece.weight,
            net_weight: piece.netWeight,
            weight_unit: piece.weightUnit,
            volume: vol,
            volume_unit: 'ccm',
            piece_product_code: piece.pieceProductCode,
            declared_value: piece.declaredValue,
            model_code: piece.modelCode, // for excel
            item_category: piece.itemCategory, // for excel
            item_size: piece.itemSize, // for excel
            volumetric_weight: piece.volumetricWeight, // for excel
            item_unit: piece.itemUnit || piece.item_unit, // for excel
            declared_currency: piece.declaredCurrency,
            pieces_item_details: transformPieceItemDetailsNew(piece.piecesItemDetails || []),
            number_of_items: piece.numberOfItems,
            piece_unique_id: piece.pieceUniqueId || null, // for excel
            cod_amount: validatedCodAmount(piece.codAmount),
            hsn_code: piece.hsnCode || piece.hsCode,
            reference_image_list: piece.referenceImageList,
            product_family: piece.productFamily,
            product_category: piece.productCategory,
            brand_name: piece.brandName,
            additional_properties: {
                ...(piece.meisApplicable !== undefined && { meis_applicable: piece.meisApplicable }),
                ...(piece.eccn !== undefined && { eccn: piece.eccn }),
                ...(piece.isKit !== undefined && { is_kit: piece.isKit }),
                ...(piece.iossNumber !== undefined && { ioss_number: piece.iossNumber }),
            },
            is_dangerous_good: piece.isDangerousGood,
            dangerous_good_accessible: piece.dangerousGoodAccessible,
            dangerous_good_un_number: piece.dangerousGoodUnNumber,
            dangerous_good_class: piece.dangerousGoodClass,
            dangerous_good_name: piece.dangerousGoodName,
            dangerous_good_division: piece.dangerousGoodDivision,
            dangerous_good_packing_group: piece.dangerousGoodPackingGroup,
            dangerous_good_category: piece.dangerousGoodCategory,
            dangerous_good_category_type: piece.dangerousGoodCategoryType,
            dangerous_good_category_packing_instruction: piece.dangerousGoodCategoryPackingInstruction,
            purchase_order_number: piece.purchaseOrderNumber,
            manufacturing_country: piece.manufacturingCountry,
            forward_reference_number: piece.forwardReferenceNumber,
            tax_details: {
                tax_percentage: piece.taxPercentage,
                total_tax: piece.totalTax,
                igst: piece.igst,
                sgst: piece.sgst,
                cgst: piece.cgst,
                receiver_gstin: piece.taxDetailsRecieverGstin,
                sender_gstin: piece.taxDetailsSenderGstin,
            },
        };
    });
};

export const transformInvoicesDetailNew = (invoicesDetailFromUpdatedData: any) => {
    return invoicesDetailFromUpdatedData.map((invoice: any) => {
        return {
            invoice_number: invoice.invoiceNumber,
            invoice_date: Helper.getEpoch(invoice.invoiceDate),
            invoice_amount: invoice.invoiceAmount,
            piece_reference_number: invoice.reference_number,
            invoice_status: invoice.invoiceStatus,
            proof_image_list: invoice.proofImageList,
            ewb_number: invoice.ewbNumber,
            sender_gstin: invoice.senderGstin,
            receiver_gstin: invoice.receiverGstin,
            ewb_creation_time: Helper.getEpoch(invoice.ewbCreationTime),
            ewb_expiry_time: Helper.getEpoch(invoice.ewbExpiryTime),
            transporter_gstin: invoice.transporterGstin,
            transporter_name: invoice.transporterName,
        };
    });
};

const convertItemDetailToItems = (items_detail: any) => {
    if (!items_detail) return undefined;
    const items: Record<any, any> = {};
    items_detail.forEach((item: any, index: number) => {
        items[`itemOld${index}`] = {
            hsnCode: item.hsn_code,
            gstValue: item.gst_value,
            ateNumber: item.ate_number,
            cessValue: item.cess_value,
            dutyValue: item.duty_value,
            itemValue: item.item_value,
            itemRate: item.item_rate,
            numberOfItems: item.no_of_items,
            skuNumber: item.sku_number,
            composition: item.composition,
            noOfItems: item.no_of_items,
            productUrl: item.product_url,
            pieceNumber: item.piece_number,
            productType: item.product_type,
            productWidth: item.product_width,
            rodtepScheme: item.rodtep_scheme,
            gstPercentage: item.gst_percentage,
            productHeight: item.product_height,
            productLength: item.product_length,
            originOfGoods: item.origin_of_goods,
            itemDescription: item.item_description,
            productDeadWeight: item.product_weight,
            itemSize: item.item_size,
            key: `itemOld${index}`,
        };
    });
    return items;
};
const convertPiecesDetailToPieces = (pieces_detail: any) => {
    const pieces: Record<any, any> = {};
    pieces_detail?.forEach((piece: any, index: number) => {
        pieces[`pieceOld${index}`] = {
            dimensionsUnit: 'cm',
            weightUnit: 'kg',
            declaredValue: piece.declared_value,
            ...piece,
            unit: piece.item_unit,
            numberOfPieces: piece.quantity || piece.item_quantity || piece.number_of_items,
            key: `pieceOld${index}`,
            items: convertItemDetailToItems(piece.item_details),
            productFamily: piece.product_family,
            productCategory: piece.product_category,
            brandName: piece.brand_name,
        };
    });
    return pieces;
};

const covertDetailsToCustomerPortal = (fullData: any) => {
    const consignment = fullData?.source === 'plugin' ? fullData?.softdata_body : fullData;
    if (!consignment) return {};
    const isCod = consignment?.is_cod ? 'yes' : 'no';
    return {
        isEditingCN: fullData?.source !== 'plugin',
        childClient: fullData?.source !== 'plugin' ? `${consignment.customer_code}+${consignment.customer_id}` : null,
        referenceNumber: consignment.reference_number,
        customerReferenceNumber: fullData?.customer_reference_number,
        shopifyFulfillmentId: consignment?.shopify_fulfillment_id,
        shopifyOrderId: consignment?.shopify_order_id,
        akNumber: consignment.ak_number,
        status: consignment.status,
        isCOD: fullData?.source !== 'plugin' ? consignment.is_cod : isCod,
        codAmount: consignment.cod_amount,
        codCollectionMode: consignment.cod_collection_mode,
        codFavorOf: consignment.cod_favor_of,
        consignmentCategory: consignment?.is_international ? 'international' : 'domestic',
        pieces: convertPiecesDetailToPieces(fullData.source === 'plugin' ? consignment?.pieces : consignment.pieces_detail),
        isInternational: consignment.is_international,
        declaredValue: consignment.declared_value,
        declaredCurrency: consignment.declared_currency,
        serviceType: consignment.service_type_id,
        previousSelectedAddress: {
            dstAddress: {
                id: 'oldDstAddress',
                name: consignment.destination_name,
                phone: consignment.destination_phone,
                alternatePhone: consignment.destination_alternate_phone,
                alternatePhone2: consignment.destination_alternate_phone_2,
                notes: consignment.destination_notes,
                addressLine1: consignment.destination_address_line_1,
                addressLine2: consignment.destination_address_line_2,
                addressLine3: consignment.destination_address_line_3,
                addressLine4: consignment.destination_address_line_4,
                addressLine5: consignment.destination_address_line_5,
                stateName: consignment.destination_state,
                cityName: consignment.destination_city,
                pincode: consignment.destination_pincode,
                countryName: consignment.destination_country,
                latitude: '',
                longitude: '',
            },
            srcAddress: {
                id: 'oldSrcAddress',
                name: consignment.sender_name,
                phone: consignment.sender_phone,
                alternatePhone: consignment.sender_alternate_phone,
                alternatePhone2: consignment.sender_alternate_phone_2,
                notes: consignment.sender_notes,
                addressLine1: consignment.sender_address_line_1,
                addressLine2: consignment.sender_address_line_2,
                addressLine3: consignment.sender_address_line_3,
                addressLine4: consignment.sender_address_line_4,
                addressLine5: consignment.sender_address_line_5,
                stateName: consignment.sender_state,
                cityName: consignment.sender_city,
                pincode: consignment.sender_pincode,
                countryName: consignment.sender_country,
                latitude: '',
                longitude: '',
            },
        },
        dstAddress: {
            id: 'oldDstAddress',
            name: consignment.destination_name,
            phone: consignment.destination_phone,
            alternatePhone: consignment.destination_alternate_phone,
            alternatePhone2: consignment.destination_alternate_phone_2,
            notes: consignment.destination_notes,
            addressLine1: consignment.destination_address_line_1,
            addressLine2: consignment.destination_address_line_2,
            addressLine3: consignment.destination_address_line_3,
            addressLine4: consignment.destination_address_line_4,
            stateName: consignment.destination_state,
            cityName: consignment.destination_city,
            pincode: consignment.destination_pincode,
            countryName: consignment.destination_country,
            latitude: '',
            longitude: '',
        },
        srcAddress: {
            id: 'oldSrcAddress',
            name: consignment.sender_name,
            phone: consignment.sender_phone,
            alternatePhone: consignment.sender_alternate_phone,
            alternatePhone2: consignment.sender_alternate_phone_2,
            notes: consignment.sender_notes,
            addressLine1: consignment.sender_address_line_1,
            addressLine2: consignment.sender_address_line_2,
            addressLine3: consignment.sender_address_line_3,
            addressLine4: consignment.sender_address_line_4,
            stateName: consignment.sender_state,
            cityName: consignment.sender_city,
            pincode: consignment.sender_pincode,
            countryName: consignment.sender_country,
            latitude: '',
            longitude: '',
        },
        isRiskSurchargeApplicable: consignment?.is_risk_surcharge_applicable,
        description: consignment?.description,
        consignmentType: consignment?.consignment_type || 'forward',
        courierType: consignment?.load_type?.toLowerCase(),
        retAddress: {
            ...consignment?.ret_address,
        },
        alternateDelAddress1: {
            ...consignment?.alternate_del_address1,
        },
        alternateDelAddress2: {
            ...consignment?.alternate_del_address2,
        },
        redirectionAddress: {
            ...consignment?.redirection_address,
        },
        commodityId: Helper.isValidArray(consignment?.commodity_id)
            ? [consignment.commodity_name, consignment?.commodity_id[0]]
            : [consignment.commodity_name, consignment?.commodity_id],
        currency: consignment?.declared_currency,
        copAmount: consignment?.cashonpickup,
        isRetailTransaction: consignment?.is_retail_transaction,
        typeOfDelivery: consignment?.type_of_delivery,
        length: consignment?.length,
        width: consignment?.width,
        height: consignment?.height,
        weight: consignment?.weight,
        kiranaHubCode: consignment?.kirana_hub_code,
        numberOfPieces: Number(consignment?.num_pieces || consignment?.no_of_pieces),
        custSellerCode: consignment?.cust_seller_code,
        shipmentPurpose: consignment?.shipment_purpose,
        shipmentPurposeDoc: consignment?.shipment_purpose,

        deliveryInstructions: consignment.delivery_instructions,
        eoriNumber: consignment.eori_number,
        ioriNumber: consignment.iori_number,
        freightCost: consignment.freight_cost,
        freightCostCurrency: consignment.freight_cost_currency,
        againstBondLut: consignment.against_bond_lut,
        ecomShipment: consignment?.ecom_shipment?.toString(),
        meis: consignment?.meis?.toString(),
        isBattery: consignment?.is_battery?.toString(),
        invoiceDate: (consignment?.invoice_date || consignment?.export_invoice_date)
            ? moment((consignment?.export_invoice_date || consignment?.invoice_date), 'YYYY-MM-DD') : undefined,
        fobValueCurrency: consignment.fob_value_currency,
        fobValue: consignment.fob_value,
        totalGSTPaidCurrency: consignment.total_gst_paid_currency,
        totalGSTPaidAmount: consignment.total_GST_paid_amount,
        consignorCompanyName: consignment.consignor_company_name,
        consignorTaxId: consignment.consignor_tax_id,
        consignorIECNumber: consignment.consignor_IEC_number,
        consignorGSTINNumber: consignment.consignor_GST_IN_number,
        consignorPanId: consignment.consignor_pan_id,
        consignorKycDocType: consignment.consignor_kyc_doc_type,
        consignorKycDocNumber: consignment.consignor_kyc_doc_number,
        consignorKycFrontImage: consignment.consignor_kyc_front_image,
        consignorKycBackImage: consignment.consignor_kyc_back_image,
        senderBankAccountNumber: consignment.sender_bank_account_number,
        senderBankADCode: consignment.sender_bank_AD_code,
        senderBankIFSCCode: consignment.sender_bank_IFSC_code,
        nefiFlag: consignment?.nefi_flag?.toString(),
        cpcCode: consignment.cpc_code,
        cessValue: consignment.cess_value,
        ewayBillNumber: consignment.eway_bill_number,
        invoiceType: consignment.invoice_type,
        invoiceNumber: consignment.invoice_number,
        consigneeCompanyName: consignment.consignee_company_name,
        deliveryTerms: consignment.delivery_terms,
        originType: consignment.origin_type,
        destinationType: consignment.destination_type,
        dimensionUnit: consignment.dimension_unit,
        weightUnit: consignment.weight_unit,
        receiverVATNumber: consignment.receiver_VAT_number,
        pickupDate: fullData?.source === 'plugin' && consignment.pickup_date
            ? moment(consignment.pickup_date, 'YYYY-MM-DD') : undefined,
        pickupTime: fullData?.source === 'plugin' && consignment.pickup_time_slot_start && consignment.pickup_time_slot_end
            ? `${consignment.pickup_time_slot_start}-${consignment.pickup_time_slot_end}` : undefined,
        unlistedCommodityName: consignment?.unlisted_commodity_name,
        ewbNumber: consignment.ewb_number,
        ewbDate: consignment?.ewb_date ? moment(consignment.ewb_date, 'YYYY-MM-DD') : undefined,
        riskSurchargeType: consignment.risk_surcharge_type ? consignment.risk_surcharge_type : undefined,
        incoTerms: consignment.inco_terms,
    };
};

export default {
    covertDetailsToCustomerPortal,
};
